/**
 * Pure search helpers for the location filter UI. Splits the
 * filter/dedup/rank logic out of the React components so the same code
 * powers the desktop sidebar (`SearchFilters`), the mobile composer
 * (`SearchComposer`), and any future surfaces — and so it's unit-testable
 * without mounting components.
 *
 * Everything is diacritic-insensitive (Vietnamese typed without diacritics
 * still matches), case-insensitive, and substring-based (not prefix-only)
 * because "ho chi" should find "Hồ Chí Minh" and "kiem" should find the
 * "Hoàn Kiếm" ward.
 */

import {
  PROVINCE_OPTIONS,
  provinceAliasKeywords,
  type Province,
  type ProvinceSlug,
} from "./provinces";
import { WARD_INDEX, normaliseQuery, type WardEntry } from "./ward-index.generated";
import { CITY_PROVINCES, isCityKeyword } from "./city-keywords";

/** Run-together form: drop spaces + hyphens so "hanoi" matches "ha noi". */
const compact = (s: string): string => s.replace(/[\s-]+/g, "");

/** A ward match that should resolve up to its parent province. */
export interface WardSuggestion {
  /** Vietnamese display name with diacritics, e.g. "Hoàn Kiếm". */
  readonly wardName: string;
  /** English ASCII display name, e.g. "Hoan Kiem". */
  readonly wardNameEn: string;
  /** Parent province (the actual filter value when picked). */
  readonly province: Province;
}

export interface SearchResult {
  /** Direct province / city matches. */
  readonly provinces: readonly Province[];
  /** Ward matches grouped by province (one per province). */
  readonly wards: readonly WardSuggestion[];
  /** Number of additional ward matches not returned (limit cap). */
  readonly wardOverflow: number;
}

/** Normalised + compact name/alias haystacks for every province, precomputed once. */
const PROVINCE_NAME_INDEX = PROVINCE_OPTIONS.map((p) => {
  // vi name, en name, slug, and colloquial aliases ("saigon", "hcmc", …),
  // each diacritic-normalised. Aliases give parity with the hero search.
  const haystacks = [
    normaliseQuery(p.name),
    normaliseQuery(p.nameEn),
    normaliseQuery(p.slug),
    ...provinceAliasKeywords(p.slug).map(normaliseQuery),
  ];
  return {
    province: p,
    haystacks,
    haystacksCompact: haystacks.map(compact),
  };
});

const PROVINCE_BY_SLUG = new Map<ProvinceSlug, Province>(
  PROVINCE_OPTIONS.map((p) => [p.slug, p]),
);

/**
 * Resolve a list of slugs to their full Province records, in the given
 * order. Unknown slugs are skipped silently — keeps the popular-chip
 * configuration in `provinces.ts` from breaking if the dataset is
 * regenerated and a slug disappears.
 */
export function provincesFromSlugs(
  slugs: readonly ProvinceSlug[],
): readonly Province[] {
  const out: Province[] = [];
  for (const slug of slugs) {
    const p = PROVINCE_BY_SLUG.get(slug);
    if (p) out.push(p);
  }
  return out;
}

/**
 * Search the province + ward catalogue for matches against `query`.
 *
 * Ranking rules:
 *   1. Province direct matches (by Vietnamese name, English name, or slug).
 *      Returned in dataset order (alphabetical by English name).
 *   2. Ward matches, deduplicated by parent province — at most one ward
 *      surfaces per province, and only if that province isn't already in
 *      the direct-match list above. Capped at `maxWards`; the count of
 *      additional matches not shown is returned as `wardOverflow` so the
 *      caller can show "+12 more" if they want.
 *
 * An empty / whitespace query returns no matches (caller should fall back
 * to showing default chips). A query under `minWardQueryLength` skips the
 * ward lookup entirely — single-character queries match too many wards to
 * be useful (e.g. "a" matches ~1,500 wards).
 */
export function searchLocations(
  query: string,
  options: { maxWards?: number; minWardQueryLength?: number } = {},
): SearchResult {
  const { maxWards = 6, minWardQueryLength = 2 } = options;

  const q = normaliseQuery(query);
  if (!q) return { provinces: [], wards: [], wardOverflow: 0 };

  // "TP" / "TP." / "Thành phố" → the user means a city; surface every
  // centrally-managed city as options (no wards).
  if (isCityKeyword(q)) {
    return { provinces: [...CITY_PROVINCES], wards: [], wardOverflow: 0 };
  }

  const qc = compact(q); // run-together query, e.g. "hanoi" / "hochiminh"

  // Province matches — substring across vi-name / en-name / slug / aliases,
  // both spaced and run-together ("hanoi" → "ha noi", "saigon" → Ho Chi Minh).
  const provinces: Province[] = [];
  for (const entry of PROVINCE_NAME_INDEX) {
    const hit =
      entry.haystacks.some((h) => h.includes(q)) ||
      (qc !== "" && entry.haystacksCompact.some((h) => h.includes(qc)));
    if (hit) provinces.push(entry.province);
  }

  if (q.length < minWardQueryLength) {
    return { provinces, wards: [], wardOverflow: 0 };
  }

  // Ward matches — dedupe by province, skip provinces already shown above.
  const alreadyShown = new Set(provinces.map((p) => p.slug));
  const wardsByProvince = new Map<ProvinceSlug, WardEntry>();
  let totalWardMatches = 0;
  for (const w of WARD_INDEX) {
    if (alreadyShown.has(w.province)) continue;
    if (
      w.normalised.includes(q) ||
      w.normalisedEn.includes(q) ||
      (qc !== "" && (compact(w.normalised).includes(qc) || compact(w.normalisedEn).includes(qc)))
    ) {
      totalWardMatches += 1;
      // Keep the FIRST ward that matched for each province (the index is
      // sorted alphabetically by normalised name, so this gives stable
      // ordering and surfaces a representative ward).
      if (!wardsByProvince.has(w.province)) {
        wardsByProvince.set(w.province, w);
      }
    }
  }

  const wards = Array.from(wardsByProvince.values())
    .slice(0, maxWards)
    .map<WardSuggestion>((w) => ({
      wardName: w.name,
      wardNameEn: w.nameEn,
      province: PROVINCE_BY_SLUG.get(w.province)!,
    }));

  const wardOverflow = Math.max(0, totalWardMatches - wards.length);
  return { provinces, wards, wardOverflow };
}
